import React from 'react';
import { FileText, Table, Presentation, File, ExternalLink, Trash2, Image, Globe } from 'lucide-react';
import { TaskFile } from '../types';

interface TaskFilesProps {
  files: TaskFile[];
  onDeleteFile?: (fileId: string) => void;
  className?: string;
}

export function TaskFiles({ files, onDeleteFile, className = '' }: TaskFilesProps) {
  if (!files || files.length === 0) return null;

  const getFileIcon = (type: TaskFile['type']) => {
    switch (type) {
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'spreadsheet':
        return <Table className="w-4 h-4" />;
      case 'presentation':
        return <Presentation className="w-4 h-4" />;
      case 'pdf':
        return <File className="w-4 h-4" />;
      case 'image':
        return <Image className="w-4 h-4" />;
      default:
        return <Globe className="w-4 h-4" />;
    }
  };

  const getFileColor = (type: TaskFile['type']) => {
    switch (type) {
      case 'document':
        return 'text-blue-600';
      case 'spreadsheet':
        return 'text-green-600';
      case 'presentation':
        return 'text-orange-600';
      case 'pdf':
        return 'text-red-600';
      case 'image':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <h4 className="text-sm font-text font-semibold text-neutral-700 flex items-center">
        <Globe className="w-4 h-4 mr-2" />
        Links & Files
      </h4>
      <div className="space-y-2">
        {files.map((file) => (
          <a
            key={file.id}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-3 bg-white/40 backdrop-blur-xl rounded-2xl border border-gray-200 hover:bg-white/60 hover:shadow-md transition-all duration-200 group"
          >
            <div className={`flex-shrink-0 ${getFileColor(file.type)}`}>
              {getFileIcon(file.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate group-hover:text-primary-600 transition-colors">
                {file.name}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-primary-500 transition-colors" />
              {onDeleteFile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDeleteFile(file.id);
                  }}
                  className="p-1 text-neutral-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
